import axios from 'axios';

/**
 * Starts a sync operation in the SAP adapter.
 * All adapter URLs must be composed using the global window.sapAdapterApi.
 * Never hardcode full URLs inside components; always call this via Vuex actions.
 *
 * @param {string} path - One of '/sync/items' | '/sync/debtors' | '/sync/stock-qty'.
 * @param {string} apiKey - X-Api-Key value for the current store/branch.
 * @param {Object} [payload] - Optional StartSyncRequest payload { mode, since, pageSize, dryRun }.
 * @returns {Promise<Object>} SyncSummary object from the adapter.
 */
export async function startSapSync(path, apiKey, payload = { mode: 'incremental', pageSize: 1000 }) {
  if (!window.sapAdapterApi) throw new Error('SAP adapter base URL is not configured (sapAdapterApi).');
  if (!apiKey) throw new Error('Missing SAP adapter API key.');
  const url = `${window.sapAdapterApi}${path.replace(/^\//, '')}`; // ensure single slash
  const { data } = await axios.post(url, payload, {
    headers: { 'X-Api-Key': apiKey },
  });
  return data;
}

/**
 * Fetches a page of debtors from the SAP adapter.
 * Uses X-Api-Key header and optional query params: page, pageSize, search, updatedSince.
 *
 * @param {string} apiKey
 * @param {Object} [params]
 * @returns {Promise<{items: Array, total: number, page: number, pageSize: number}>}
 */
export async function fetchAdapterDebtors(apiKey, params = {}) {
  if (!window.sapAdapterApi) throw new Error('SAP adapter base URL is not configured (sapAdapterApi).');
  if (!apiKey) throw new Error('Missing SAP adapter API key.');
  const url = `${window.sapAdapterApi}debtors`;
  const { data } = await axios.get(url, {
    headers: { 'X-Api-Key': apiKey },
    params,
  });
  // Normalize various possible response shapes to a unified structure
  // Accepts: { items: [...] } OR { debtors: [...], nextPageToken, maxUpdateDate } OR raw array
  const items = Array.isArray(data) ? data
    : (Array.isArray(data?.items) ? data.items
    : (Array.isArray(data?.debtors) ? data.debtors : []));
  const page = typeof data?.page === 'number' ? data.page : (params.page || 1);
  const pageSize = typeof data?.pageSize === 'number' ? data.pageSize : (params.pageSize || 100000);
  const total = typeof data?.total === 'number' ? data.total : items.length;
  return { items, total, page, pageSize };
}

/**
 * Fetches a page of items from the SAP adapter.
 * Uses X-Api-Key header and optional query params: page, pageSize, search, updatedSince.
 *
 * @param {string} apiKey
 * @param {Object} [params]
 * @returns {Promise<{items: Array, total: number, page: number, pageSize: number}>}
 */
export async function fetchAdapterItems(apiKey, params = {}) {
  if (!window.sapAdapterApi) throw new Error('SAP adapter base URL is not configured (sapAdapterApi).');
  if (!apiKey) throw new Error('Missing SAP adapter API key.');
  const url = `${window.sapAdapterApi}items`;
  const { data } = await axios.get(url, {
    headers: { 'X-Api-Key': apiKey },
    params,
  });
  // Normalize various possible response shapes to a unified structure
  // Accepts: { items: [...] , nextPageToken?, maxUpdateDate? } OR raw array OR occasionally { products: [...] }
  const items = Array.isArray(data) ? data
    : (Array.isArray(data?.items) ? data.items
    : (Array.isArray(data?.products) ? data.products : []));
  const page = typeof data?.page === 'number' ? data.page : (params.page || 1);
  const pageSize = typeof data?.pageSize === 'number' ? data.pageSize : (params.pageSize || 100000);
  const total = typeof data?.total === 'number' ? data.total : items.length;
  const nextPageToken = typeof data?.nextPageToken === 'string' ? data.nextPageToken : null;
  const maxUpdateDate = typeof data?.maxUpdateDate === 'string' ? data.maxUpdateDate : null;
  return { items, total, page, pageSize, nextPageToken, maxUpdateDate };
}
